import { TabContext, TabPanel } from '@mui/lab';
import { Box, Stack, Tab, Tabs, useTheme } from '@mui/material';
import { RequestsTabType } from 'constants/requests.constants';
import { useAppDispatch, useAppSelector } from 'hooks/redux.hooks';
import _ from 'lodash';
import { Fragment, useEffect, useMemo, useState } from 'react';
import AwaitingAuth from 'sections/request/AwaitingAuth';
import OpenPending from 'sections/request/OpenPending';
import { getOpenPendingRequestsByCompanyId } from 'services/requests.services';
import { getAwaitingAuthorization, getOpenPendingRequests, isLoading } from 'state/reducers/requests.reducers';
import { selectTranslations } from 'state/reducers/translation.reducers';
import { HeaderPage1 } from 'theme/elements/Headers';
// components
import { Page } from 'theme/elements/Page';
import { CustomTabs } from 'theme/elements/Tabs';
import { useLocation } from 'react-router-dom';
import WaitingMessage from 'components/WaitingMessage';
import { getBalancesByCompanyId } from 'services/balance.services';

// sections

// reducers

export const RequestsTabComponents = {
	AwaitingAuth: <AwaitingAuth />,
	OpenPending: <OpenPending />,
};

export default function RequestApp() {
	const t = useAppSelector(selectTranslations);
	const openPendingRequests = useAppSelector(getOpenPendingRequests);
	const awaitingAuthorizationRequests = useAppSelector(getAwaitingAuthorization);
	const [currentTabIdx, setCurrentTabIdx] = useState<string>('0');
	const dispatch = useAppDispatch();
	const theme = useTheme();
	const location = useLocation();
	const isOpenRequests = location.state?.isOpenRequests;
	const handleTabChange = (event: React.SyntheticEvent, val: number) => {
		setCurrentTabIdx(String(val));
	};
	const loading: boolean = useAppSelector(isLoading);

	const openPendingRequestsCount = useMemo(() => {
		return _.flatten(_.values(openPendingRequests)).length;
	}, [openPendingRequests]);

	const awaitingAuthorizationRequestsCount = useMemo(() => {
		return _.flatten(_.values(awaitingAuthorizationRequests)).length;
	}, [awaitingAuthorizationRequests]);

	useEffect(() => {
		if (isOpenRequests) {
			setCurrentTabIdx(String(1));
		}
	}, [isOpenRequests]);

	useEffect(() => {
		dispatch(getOpenPendingRequestsByCompanyId());
		dispatch(getBalancesByCompanyId());
	}, []);

	if (loading) {
		return <WaitingMessage />;
	}
	return (
		<Fragment>
			<Page>
				<Fragment>
					<Stack spacing={1}>
						<HeaderPage1 title={t('page.pending_requests.title')} />
						<TabContext value={currentTabIdx}>
							<Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
								<CustomTabs>
									<Tabs value={+currentTabIdx} onChange={handleTabChange}>
										{_.values(RequestsTabType).map((val, idx) => {
											let amount =
												val === RequestsTabType.OpenPending
													? openPendingRequestsCount
													: awaitingAuthorizationRequestsCount;
											return (
												<Tab
													key={val}
													id={val}
													tabIndex={idx}
													label={t(`page.pending_requests.tab.${_.snakeCase(val)}.title`)}
													sx={{
														'&:after': {
															content: `"${amount}"`,
															color: +currentTabIdx == idx ? '#fff' : 'inherit',
															backgroundColor:
																+currentTabIdx == idx ? '#f75d12' : '#e7edee',
														},
													}}
												/>
											);
										})}
									</Tabs>
								</CustomTabs>
							</Box>
							{_.values(RequestsTabType).map((val, idx) => (
								<TabPanel
									sx={{ [theme.breakpoints.down('sm')]: { padding: 0 } }}
									key={val}
									value={'' + idx}
								>
									{RequestsTabComponents[val]}
								</TabPanel>
							))}
						</TabContext>
					</Stack>
				</Fragment>
			</Page>
		</Fragment>
	);
}
